import { useState, useRef, useEffect } from 'react';
import { Printer, Loader2, Trash2, ArrowLeft, Building, Mail, Phone, MapPin, FileText, User, Calendar, DollarSign, ClipboardCheck } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import BillsService from '@/api/bills.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import FormModal from '@/components/FormModal';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

// Update the Supplier type to match the schema
type Supplier = {
  id: number;
  publicId: string;
  businessId: number;
  name: string;
  contactPerson: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  taxId: string;
  notes: string;
  status: string; // active, inactive
  timezone: string;
  dateUpdated: string;
  dateCreated: string;
};

// Bill type definition matching the schema
type Bill = {
  id: number;
  billNumber: string;
  userId: number;
  businessId: number;
  amount: number;
  currency: string;
  status: string; // paid, unpaid, partially_paid, overdue
  issueDate: string;
  dueDate: string;
  timezone: string;
  dateCreated: string;
};

// BillItem type definition
type BillItem = {
  id: number;
  billId: number;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
};

export default function Bill({ billId }: { billId: string }) {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [items, setItems] = useState<BillItem[]>([{ id: 0, billId: 0, name: '', description: '', quantity: 1, unitPrice: 0, totalPrice: 0 }]);
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
  });
  const [supplierDetails, setSupplierDetails] = useState<Supplier | null>(null);
  const [userDetails, setUserDetails] = useState({ name: '', email: '' });
  const [taxes, setTaxes] = useState([{ name: '', rate: 0, taxAmount: 0 }]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [billNumber, setBillNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  
  const printableRef = useRef<PrintableRef | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  useEffect(() => {
    const fetchBill = async () => {
      setIsLoading(true);
      try {
        const response = await BillsService.getBillByPublicId(billId);
        const { data } = response;
        console.log("getBillById data", data);
        if (data.result === 'success') {
          const bill = data.bill;
          setCompanyDetails(bill.business);
          setSupplierDetails(bill.supplier);
          setUserDetails(bill.user);
          setItems(bill.billItems);
          setTaxes(bill.billTaxes);
          setNotes(bill.notes);
          setTerms(bill.terms);
          setSignature(bill.signature);
          setCurrency(bill.currency);
          setStatus(bill.status);
          setBillNumber(bill.billNumber);
          setIssueDate(bill.issueDate);
          setDueDate(bill.dueDate);
        } else {
          showToastWithTimeout("error", "❌ Fetch Bill Failed", data.message, 3000);
        }
      } catch (error) {
        console.error(error);
        showToastWithTimeout("error", "❌ Fetch Bill Failed", "An error occurred while fetching the bill.", 3000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBill();
  }, [billId]);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        state: selectedBusiness.state,
        zipCode: selectedBusiness.zipCode,
        country: selectedBusiness.country,
        phone: selectedBusiness.phoneNumber,
        email: selectedBusiness.emailAddress,
        website: selectedBusiness.companyName,
      });
      setLogo(selectedBusiness.logoImg);
    }
  }, [selectedBusiness]);

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await BillsService.deleteBill(parseInt(billId));
      if (response.data.result === 'success') {
        showToastWithTimeout("default", "✅ Bill Deleted", "Bill has been successfully deleted", 3000);
        setTimeout(() => {
          navigate('/bills');
        }, 2000);
      } else {
        showToastWithTimeout("error", "❌ Delete Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Delete Failed", "An error occurred while deleting the bill", 3000);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const calculateTotalTax = () => {
    return taxes.reduce((sum, tax) => sum + tax.taxAmount, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalTax();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partially_paid':
        return 'bg-amber-100 text-amber-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      case 'overdue':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const document = {
    id: billId,
    number: billNumber,
    type: 'bill',
    date: issueDate,
    dueDate: dueDate,
    currency,
    items,
    taxes,
    notes,
    terms,
    logo,
    signature,
    status,
    business: companyDetails,
    supplier: supplierDetails,
    user: userDetails,
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Bill #{billNumber}</h1>
          <Badge className={getStatusColor(status)}>{status}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button
            size="sm"
            onClick={handlePrint}
            className="flex items-center gap-1"
          >
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      <FormModal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title="Delete Bill"
      >
        <div className="space-y-4">
          <p className="text-sm">Are you sure you want to delete this bill? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} size="sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting} size="sm">
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </div>
        </div>
      </FormModal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building className="h-4 w-4" />
              Supplier Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {supplierDetails ? (
              <>
                <div className="font-medium">{supplierDetails.name}</div>
                {supplierDetails.contactPerson && (
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>{supplierDetails.contactPerson}</span>
                  </div>
                )}
                {supplierDetails.email && (
                  <div className="flex items-start gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>{supplierDetails.email}</span>
                  </div>
                )}
                {supplierDetails.phoneNumber && (
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>{supplierDetails.phoneNumber}</span>
                  </div>
                )}
                {supplierDetails.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div>{supplierDetails.address}</div>
                      <div>
                        {supplierDetails.city}{supplierDetails.city && supplierDetails.state ? ', ' : ''}
                        {supplierDetails.state}
                      </div>
                      <div>
                        {supplierDetails.postalCode}{supplierDetails.postalCode && supplierDetails.country ? ', ' : ''}
                        {supplierDetails.country}
                      </div>
                    </div>
                  </div>
                )}
                {supplierDetails.taxId && (
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span>Tax ID: {supplierDetails.taxId}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted-foreground">No supplier information available</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Bill Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Bill Number:</span>
              <span className="font-medium">{billNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Issue Date:</span>
              <span>{formatDate(issueDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Due Date:</span>
              <span>{formatDate(dueDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status:</span>
              <Badge className={getStatusColor(status)}>{status}</Badge>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal:</span>
              <span>{currency} {calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tax:</span>
              <span>{currency} {calculateTotalTax().toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Total:</span>
              <span>{currency} {calculateTotal().toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Payment Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Currency:</span>
              <span>{currency}</span>
            </div>
            {notes && (
              <div className="mt-4">
                <h4 className="font-medium mb-1">Notes:</h4>
                <p className="text-sm text-muted-foreground">{notes}</p>
              </div>
            )}
            {terms && (
              <div className="mt-4">
                <h4 className="font-medium mb-1">Terms:</h4>
                <p className="text-sm text-muted-foreground">{terms}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Bill Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{item.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{currency} {item.unitPrice.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{currency} {(item.quantity * item.unitPrice).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50">
                  <td colSpan={4} className="px-4 py-3 text-sm font-medium text-gray-900 text-right">Subtotal:</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{currency} {calculateSubtotal().toFixed(2)}</td>
                </tr>
                {taxes.map((tax, index) => (
                  <tr key={index} className="bg-gray-50">
                    <td colSpan={3} className="px-4 py-3 text-sm text-gray-900 text-right">{tax.name}:</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{tax.rate}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{currency} {tax.taxAmount.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100">
                  <td colSpan={4} className="px-4 py-3 text-sm font-bold text-gray-900 text-right">Total:</td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">{currency} {calculateTotal().toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="hidden">
        <PrintableDocument
          ref={printableRef}
          document={{
            id: billId,
            number: billNumber,
            type: 'bill',
            date: issueDate,
            dueDate: dueDate,
            currency,
            items,
            taxes,
            notes,
            terms,
            logo,
            signature,
            status,
            companyDetails,
            supplierDetails: {
              companyName: supplierDetails?.name,
              name: supplierDetails?.contactPerson || supplierDetails?.name || '',
              address: `${supplierDetails?.address || ''}, ${supplierDetails?.city || ''}, ${supplierDetails?.state || ''}, ${supplierDetails?.country || ''}`,
              emailAddress: supplierDetails?.email || '',
              phoneNumber: supplierDetails?.phoneNumber || '',
            },
          }}
        />
      </div>
    </div>
  );
}